import React, { useState, useCallback, useEffect } from 'react'
import cn from 'classnames'
import { initials } from 'utils'
import { debounce } from 'lodash'
import api from 'api/api'
import useStore from 'state/knovStore'
import styles from 'components/teams/team-settings.module.scss'

const failedUrls = new Set()

function isExpectedLogoRefreshError(error) {
    return [401, 403, 404].includes(error?.status)
}

async function getTeamForLogoRefresh(team) {
    const teamId = team?.id
    if (!teamId) return null

    if (gon.currentUser) {
        return api.getTeam(teamId)
    }

    const publicTeams = useStore.getState().publicTeams || []
    const cachedPublicTeam = publicTeams.find(
        publicTeam => String(publicTeam.id) === String(teamId),
    )
    if (cachedPublicTeam) return cachedPublicTeam

    return api.getPublicTeam(teamId, team?.space_id ? { space_id: team.space_id } : null)
}

export default function TeamLogo(props) {
    const {
        team,
        contextStyles,
        hoverStyles,
        selectedStyles,
        borderStyles,
        initialStyles,
        quest,
        fullSize,
    } = props
    const name = team.name
    // The thumb covers icons up to about 60px. A failed one falls back to the
    // original, now and on later mounts.
    const thumbUrl = fullSize ? null : team?.team_image?.thumb_url
    const teamLogo =
        (thumbUrl && !failedUrls.has(thumbUrl) ? thumbUrl : team?.team_image?.url) || null
    const [imageUrl, setImageUrl] = useState(() =>
        teamLogo && !failedUrls.has(teamLogo) ? teamLogo : null,
    )
    const imageStyles = imageUrl ? styles.imageStyles : null

    // Keep local state in sync if parent provides a newer URL (e.g., after a refresh)
    useEffect(() => {
        if (!teamLogo) return setImageUrl(null)
        setImageUrl(currentUrl => {
            if (failedUrls.has(teamLogo)) return currentUrl === teamLogo ? null : currentUrl
            return currentUrl === teamLogo ? currentUrl : teamLogo
        })
    }, [teamLogo])

    const handleImgError = useCallback(
        debounce(
            async img => {
                try {
                    const imgUrl = img?.currentSrc || img?.src
                    if (!imgUrl || failedUrls.has(imgUrl)) return
                    failedUrls.add(imgUrl)

                    const questId = quest?.id
                    if (questId) {
                        const freshQuest = await api.getQuestQuery(questId)
                        const freshUrl = freshQuest?.team?.team_image?.url
                        if (freshUrl && freshUrl !== imgUrl) {
                            setImageUrl(freshUrl)
                            return
                        }
                    }

                    const teamId = team?.id
                    if (teamId) {
                        const freshTeam = await getTeamForLogoRefresh(team)
                        const freshUrl = freshTeam?.team_image?.url
                        if (freshUrl && freshUrl !== imgUrl) {
                            setImageUrl(freshUrl)
                            return
                        }
                    }

                    // If we couldn't recover a working URL, fall back to initials.
                    setImageUrl(null)
                } catch (error) {
                    if (!isExpectedLogoRefreshError(error)) {
                        console.warn('Failed to refresh team logo image URL:', error)
                    }
                    setImageUrl(null)
                }
            },
            300,
            { leading: true },
        ),
        [quest?.id, team?.id, team?.space_id],
    )

    return (
        <div
            className={cn(
                styles.teamLogoComp,
                imageStyles,
                contextStyles,
                hoverStyles,
                selectedStyles,
                borderStyles,
            )}
        >
            {imageUrl ? (
                <img
                    src={imageUrl}
                    loading="lazy"
                    onError={ev => handleImgError(ev.currentTarget)}
                />
            ) : (
                <div className={cn(styles.teamInitial, initialStyles)}>{initials(name)}</div>
            )}
        </div>
    )
}
